// The agent-sync stack's state between calls, owned per scope so one reset drops it all
// (canon:NO-STATIC-STATE, #3629).

export interface SessionPart {
  reset(): void;
  size?(): number;
}

export class SyncSession {
  readonly #parts = new Map<string, SessionPart>();
  // name -> the value that part resets (what a guard compares a module binding against).
  readonly #values = new Map<string, unknown>();

  constructor(readonly scope: string) {}

  own<T extends SessionPart>(name: string, part: T): T {
    return this.register(name, part, part);
  }

  // A store that resets itself its own way (a singleton with a clear method).
  adopt<T>(name: string, value: T, reset: (value: T) => void): T {
    return this.register(name, value, { reset: () => reset(value) });
  }

  collection<T extends { clear(): void; readonly size: number }>(
    name: string,
    held: T,
    onReset?: (held: T) => void,
  ): T {
    return this.register(name, held, {
      reset: () => {
        onReset?.(held);
        held.clear();
      },
      size: () => held.size,
    });
  }

  private register<T>(name: string, value: T, part: SessionPart): T {
    if (this.#parts.has(name)) throw new Error(`${this.scope}: "${name}" is already owned`);
    this.#parts.set(name, part);
    this.#values.set(name, value);
    return value;
  }

  valueOf(name: string): unknown {
    return this.#values.get(name);
  }

  reset(): void {
    for (const part of this.#parts.values()) part.reset();
  }

  held(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [name, part] of this.#parts) if (part.size) out[name] = part.size();
    return out;
  }

  names(): string[] {
    return [...this.#parts.keys()];
  }
}

// Keyed by a conversation: reset by agentStreamState.clearAll(), and so by logout.
export const conversationSync = new SyncSession("conversation");
// What the account learned about daemons, rosters and DM surfaces: reset by logout.
export const accountSync = new SyncSession("account");
// What a surface learned inside one workspace: reset when another workspace is selected.
export const workspaceSync = new SyncSession("workspace");
